import _cloneDeep from 'lodash/cloneDeep';
import _uniq from 'lodash/uniq';
import {
  CODE_ACTION_PLAN,
  SYSTEM_ACTION_PLAN,
  SYSTEM_ACTION_PLAN_LAST_EDITED_BY,
  SYSTEM_CARE_PLAN_COMPOSITE_REPORT_DEFINITION_DURABLE_ID,
  SYSTEM_LIFEOMIC_DATASET,
  SYSTEM_LIFEOMIC_URL,
} from '../../../../util/fhir';
import { AI_GENERATED_SYSTEM } from './utils';

import querystring from 'query-string';
import { ImpressionItem } from '../AITools/ImpressionEditor/ImpressionItem';
import { SaveOptions } from '../AITools/ImpressionEditor/types';

const CARE_PLAN_AI_GENERATED_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-ai-generated-trace-id';

export class CarePlanItem {
  constructor(public _carePlan: fhir.CarePlan) {}

  get traceId() {
    const extensions = this._carePlan.extension || [];
    return extensions.find(
      extension => extension.url === CARE_PLAN_AI_GENERATED_SYSTEM,
    )?.valueString;
  }
}

export class ExecutiveSummaryData {
  impressions: ImpressionItem[];
  carePlan: fhir.CarePlan;
  constructor(impressions: fhir.ClinicalImpression[], carePlan: fhir.CarePlan) {
    const impressionItems = impressions.map(
      impression => new ImpressionItem(impression),
    );

    const getImpressionPosition = (impression: ImpressionItem) =>
      carePlan.supportingInfo?.findIndex(
        info => info.reference?.split('/')[1] === impression.id,
      ) ?? -1;
    this.impressions = impressionItems.sort(
      (a, b) => getImpressionPosition(a) - getImpressionPosition(b),
    );

    this.carePlan = carePlan;
  }

  get hasContent() {
    return this.impressions.length > 0 || !!this.carePlan.activity?.length;
  }

  get impressionTypes() {
    return _uniq(this.impressions.map(impression => impression.impressionType));
  }

  get status() {
    return this.carePlan.status;
  }

  get attachments() {
    return _uniq(
      this.impressions
        .map(impression =>
          impression.attachments
            .filter(attachment => !!attachment.reference)
            .flatMap(attachment => attachment.reference as string),
        )
        .filter(Boolean)
        .flat(),
    );
  }

  setId(id: string) {
    this.carePlan.id = id;
  }

  setDuration({ start, end }: { start?: Date; end?: Date }) {
    this.carePlan.period = {
      ...this.carePlan.period,
      start: start?.toISOString() || this.carePlan.period?.start,
      end: end?.toISOString() || this.carePlan.period?.end,
    };
  }

  setStatus(status: string) {
    this.carePlan.status = status;
  }

  getDuration(): { start: Date | undefined; end: Date | undefined } {
    return {
      start: this.carePlan.period?.start
        ? new Date(this.carePlan.period.start)
        : undefined,
      end: this.carePlan.period?.end
        ? new Date(this.carePlan.period.end)
        : undefined,
    };
  }

  setKeyFindings(findings: string[]) {
    this.carePlan.addresses = findings.map(finding => ({
      display: finding,
    }));

    this.carePlan.activity?.forEach(activity => {
      if (!activity?.detail?.reasonReference) return;
      const existing = activity.detail.reasonReference;
      const parent = this.carePlan.addresses || [];
      // set activity.detail.reasonReference to the intersection of existing and parent
      activity.detail.reasonReference = existing.filter(r =>
        parent.find(p => p.display === r.display),
      );
    });
  }

  get traceId() {
    const extensions = this.carePlan.extension || [];
    return extensions.find(
      extension => extension.url === CARE_PLAN_AI_GENERATED_SYSTEM,
    )?.valueString;
  }

  get compositeReportDefinitionDurableId(): string | undefined {
    const crdExt = this.carePlan.extension?.find(
      ext =>
        ext.url === SYSTEM_CARE_PLAN_COMPOSITE_REPORT_DEFINITION_DURABLE_ID,
    );
    if (crdExt) return crdExt.valueString as string;
  }

  copy() {
    return new ExecutiveSummaryData(
      _cloneDeep(this.impressions.map(i => i.impression)),
      _cloneDeep(this.carePlan),
    );
  }

  hasKeyFinding(finding: string): boolean {
    return !!this.getKeyFindings().find(
      (f: string) => f.toLowerCase() === finding.toLowerCase(),
    );
  }

  removeImpression(impressionId: string) {
    this.impressions = this.impressions.filter(
      impression => impression.id !== impressionId,
    );
    this.carePlan.supportingInfo = this.carePlan.supportingInfo?.filter(
      info => info.reference?.split('/')[1] !== impressionId,
    );
  }

  getKeyFindings(): string[] {
    return (this.carePlan?.addresses || [])
      .map(addr => addr?.display)
      .filter((d): d is string => !!d);
  }

  addActionItem(activity: fhir.CarePlanActivity) {
    if (!this.carePlan.activity) {
      this.carePlan.activity = [];
    }

    this.carePlan.activity.push(activity);
  }

  removeActionItem(activity: fhir.CarePlanActivity) {
    const existing = this.getActionItem(activity);
    if (!existing) return;
    this.carePlan.activity = this.carePlan.activity?.filter(
      a => a !== existing,
    );
  }

  updateActionItem(
    previousActivity: fhir.CarePlanActivity,
    activity: fhir.CarePlanActivity,
  ) {
    if (!this.carePlan.activity) {
      return this.carePlan.activity;
    }
    const existing = this.getActionItem(previousActivity);
    if (!existing?.detail) return;
    activity.detail?.extension?.forEach(ext => {
      // if the activity has been manually edited, it is no longer AI generated
      if (ext.url === AI_GENERATED_SYSTEM) {
        ext.valueBoolean = false;
      }
    });
    this.carePlan.activity = [
      ...this.carePlan.activity.filter(a => a !== existing),
      activity,
    ];
  }

  getActionItem(
    activity: fhir.CarePlanActivity,
  ): fhir.CarePlanActivity | undefined {
    // This is a hack to find the activity in the care plan.
    // Ideally we'd use a fhir ID but these don't have ids.
    return this.carePlan.activity?.find(
      a => a.detail?.description === activity.detail?.description,
    );
  }

  toggleActionItemStatus(activity: fhir.CarePlanActivity) {
    const existing = this.getActionItem(activity);
    if (!existing) return;

    activity.detail!.status =
      activity.detail!.status === 'unknown' ? 'on-hold' : 'unknown';
  }

  setLinkExtension(query: {
    account: string | undefined;
    projectId: string;
    dashboardId: string;
  }) {
    const urlExtension = {
      url: SYSTEM_LIFEOMIC_URL,
      valueUri: querystring.stringifyUrl({
        url:
          'https://fountainlife.apps.us.lifeomic.com' +
          window.location.pathname,
        query,
      }),
    };

    if (this.carePlan.extension?.some(e => e.url === SYSTEM_LIFEOMIC_URL)) {
      this.carePlan.extension = this.carePlan.extension?.map(e =>
        e.url === SYSTEM_LIFEOMIC_URL ? urlExtension : e,
      );
    } else {
      this.carePlan.extension = (this.carePlan.extension || []).concat(
        urlExtension,
      );
    }
  }

  setVersion(versionId: string | undefined) {
    this.carePlan.meta = {
      ...this.carePlan.meta,
      versionId,
    };
  }

  setLastEditedBy(userId: string | undefined) {
    // This isn't meant to be a very robust solution, just a quick way to
    // start tracking who last edited the care plan.
    // Ideally, we'd associate the current user with a Practitioner resource
    // and attach that to the CarePlan as the `author`.
    const ext = this.carePlan.extension?.find(
      ext => ext.url === SYSTEM_ACTION_PLAN_LAST_EDITED_BY,
    );
    if (ext) {
      ext.valueString = userId;
      return;
    }
    this.carePlan.extension?.push({
      url: SYSTEM_ACTION_PLAN_LAST_EDITED_BY,
      valueString: userId,
    });
  }

  getLastEditedBy(): string {
    return (
      this.carePlan.extension?.find(
        ext => ext.url === SYSTEM_ACTION_PLAN_LAST_EDITED_BY,
      )?.valueString || ''
    );
  }
}

export const emptyExecutiveSummary = ({
  projectId,
  patientId,
}: {
  projectId: string;
  patientId: string;
}) =>
  new ExecutiveSummaryData([], {
    resourceType: 'CarePlan',
    status: 'active',
    intent: 'proposal',
    category: [
      {
        coding: [
          {
            system: SYSTEM_ACTION_PLAN,
            code: CODE_ACTION_PLAN,
          },
        ],
      },
    ],
    subject: {
      reference: `Patient/${patientId}`,
    },
    meta: {
      tag: [
        {
          system: SYSTEM_LIFEOMIC_DATASET,
          code: projectId,
        },
        {
          system: SYSTEM_ACTION_PLAN,
          code: 'standard',
        },
      ],
    },
    addresses: [],
    activity: [],
  });

export type EditingState =
  | { isEditing: false }
  | {
      isEditing: true;
      isSaving: boolean;
      onSave: (saveOptions?: SaveOptions) => void;
      onDiscard: () => void;
    };

export type FindingsState = { findings: string[] };
export { ImpressionItem };
